import { CommonModule } from '@angular/common';
import { Component, inject, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { SasAutocompleteDirective } from '@directives';
import { SasDynamicFormService } from '../sas-dynamic-form.service';
import { FormSubmittedService } from '@services';
import { normalizeTime, TIME_PATTERN } from '@constants';

@Component({
    selector: 'sas-input-text-field-type',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormlyModule, TranslateModule, SasAutocompleteDirective],
    providers: [],
    template: `
        <div class="form-group mb-3">
            <label [for]="field.id"
                   [id]="field.id + 'Label'"
                   class="form-label"
                   [textContent]="field.props.label"
                   translate="{{field.props.label}}">
            </label>
            @if (field.props.required) {
            <span> *</span>
            }
            <div [id]="field.id + 'Help'" [title]="helpText" class="sas-inline-block">
                @if (helpText.length) {
                <i class="bi bi-info-circle-fill sas-info-icon" aria-hidden="true"></i>
                }
            </div>
            <input type="text"
                   [name]="field.name"
                   class="form-control"
                   [ngClass]="{'is-invalid': isInvalid}"
                   [id]="field.id"
                   [formControl]="formControl"
                   [placeholder]= "!isReadonly ? (field.props.placeholder! | translate) : ''"
                   maxlength="{{field.props.maxLength}}"
                   sasAutoComplete
                   tabindex="0"
                   [required]="field.props.required ?? false"
                   [readOnly]="isReadonly"
                   [attr.pattern]="field.props['validation']?.pattern || null"
                   [attr.aria-invalid]="isInvalid"
                   [attr.aria-labelledby]="field.id + 'Label'"
                   [attr.aria-describedby]="field.id + 'Help'"
                   (focus)="hasFocus = true"
                   (blur)="onBlur()"/>
            <div [id]="field.id + 'Desc'" role="alert" aria-atomic="true" class="invalid-feedback">
                <small>{{ sasDynamicFormService.getFieldErrorMessage(field) }}</small>
            </div>
        </div>
    `,
    styleUrl: './sas-input-text-field-type.component.scss'
})

export class SasInputTextFieldTypeComponent extends FieldType<FieldTypeConfig> {
    formSubmittedService = inject(FormSubmittedService);
    translate = inject(TranslateService);
    sasDynamicFormService = inject(SasDynamicFormService);

    helpText: string = '';
    hasFocus = false;

    get isReadonly(): boolean {
        return (this.field.formControl.disabled || this.field.props.readonly) ?? false;
    }

    get isTimeFormat(): boolean {
        return !!this.field.props['timeFormat'];
    }

    get isInvalid() {
        // Time fields are only formatted on blur, so hide errors while the user is still typing
        const dirty = this.field.formControl.dirty && !(this.isTimeFormat && this.hasFocus);
        return this.field.formControl.invalid && (dirty || this.formSubmittedService.isSubmitted())
    }

    onBlur(): void {
        this.hasFocus = false;

        const value = this.formControl.value;

        if (!this.isTimeFormat || this.isReadonly || typeof value !== 'string') return;

        const formatted = normalizeTime(value);

        if (formatted && formatted !== value) {
            this.formControl.setValue(formatted);
        }
    }

    ngOnInit() {
        if (this.field.props['helptext']) {
            const helpText = this.translate.instant(this.field.props['helptext']);
            this.helpText = helpText === this.field.props['helptext'] ? '' : helpText;
        }

        // [attr.pattern] on the template only drives the browser's own native constraint
        // validation — it never touches formControl.errors, so add a real validator too.
        const pattern = this.isTimeFormat ? TIME_PATTERN : this.field.props['validation']?.pattern;

        if (pattern) {
            this.formControl.addValidators(Validators.pattern(pattern));
            this.formControl.updateValueAndValidity();
        }
    }
}
